import { ActionSequence } from 'src/lib/ActionSequence';
import statePromise from 'src/lib/statePromise';
import StateObserver from 'src/StateObserver';
import { MinigameType } from 'src/replicant/minigame/minigame.ruleset';
import { EntityManager } from '../logic/EntityManager';
import { animDuration, waitForAssets, waitForItPromise } from 'src/lib/utils';
import { match3Assets } from 'src/loadingGroups';
import { match3Actions } from '../minigame/match3/match3.redux';
import { Match3 } from 'src/replicant/minigame/match3';
import { assertNever } from 'src/replicant/utils';
import { createPersistentEmitter } from 'src/lib/Emitter';
import sounds from 'src/lib/sounds';
import { duration } from 'src/replicant/utils/duration';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import { Minigame } from 'src/replicant/minigame';
import { Inventory } from 'src/replicant/buruburu/inventory';
import { analytics } from '@play-co/gcinstant';
import { getPet } from '../pet';
import { Feature } from 'src/replicant/analytics';
import { Tutorial } from 'src/replicant/buruburu/tutorial';
import { getDynamicTestBucket } from 'src/replicant/getters/ab';
import ab from 'src/replicant/ruleset/ab';
import { Social } from 'src/replicant/social';
import { socialSequences } from '../social/social.sequence';
import { getTutorialController } from '../tutorial/TutorialController';
import { socialActions } from '../social/social.redux';
import { runShopOutOfEnergySequence } from '../shop/shop.sequence';
import { iapClient } from 'src/lib/iap/iapClient';

export async function startMinigame(minigame: MinigameType) {
  // Feature disabled, bail.
  const { isMinigameAvailable } = Minigame.getters(
    StateObserver.getState().user,
  );
  if (!isMinigameAvailable(minigame)) {
    return;
  }

  await ActionSequence.start(
    [
      // Await for tutorial to be completed.
      async () => {
        await getTutorialController().handleEvent('match3Started');
        return false;
      },

      // Start minigame and consume energy
      async () => {
        const energyRequired = Minigame.ruleset.getEnergyRequirement(minigame);
        const now = StateObserver.now();
        const { user } = StateObserver.getState();
        if (Inventory.getters(user).getEnergy(now) < energyRequired) {
          if (iapClient.paymentsAvailable) {
            // Not enough energy, show energy shop and cancel sequence if didn't buy any
            const hasEnergy = await runShopOutOfEnergySequence(energyRequired);
            if (!hasEnergy) return true;
          } else {
            return true;
          }
        }

        await StateObserver.invoke.startMinigame(minigame);
        analytics.pushEvent('MinigameStart', { gameName: minigame });
        return false;
      },

      // Load minigame scene.
      async () => {
        await loadMinigame(minigame);

        await getPet().moveTo({
          scene: 'minigame',
          location: 'platform',
        });

        return false;
      },

      // Create persistent emitter for pet appearance during game
      // but unbind when pet leaves minigame scene
      async () => {
        const setPetAppearance = (multiplier) => {
          getPet().appearance.setAction(multiplier > 1 ? 'joy' : 'idle');
        };
        const emitter = createPersistentEmitter(({ user }) =>
          Match3.getters(user).getComboMultiplier(),
        );
        emitter.addListener(setPetAppearance);
        EntityManager.createOrGetPlayerPet().on('ViewRemoved', () =>
          emitter.removeListener(setPetAppearance),
        );

        return false;
      },

      // Play minigame
      async () => {
        const { setPuzzleReadyState, setPlaying } = match3Actions;
        // Show ready overlay.
        StateObserver.dispatch(setPuzzleReadyState('ready'));
        await waitForItPromise(duration({ seconds: 3 }));

        // Show go overlay.
        StateObserver.dispatch(setPuzzleReadyState('go'));
        await waitForItPromise(duration({ seconds: 1 }));

        // Enable matching.
        StateObserver.dispatch(setPlaying(true));

        // Hide overlay
        StateObserver.dispatch(setPuzzleReadyState('hidden'));

        // Wait until there are 10 seconds left.
        await waitForItPromise(
          Match3.ruleset.puzzleDuration - duration({ seconds: 10 }),
        );
        // Start countdown SFX.
        sounds.sfx.play('timer', { loop: true });
        await waitForItPromise(duration({ seconds: 5 }));
        sounds.sfx.stop('timer');

        // Intensify countdown SFX.
        sounds.sfx.play('timerFast', { loop: true });
        await waitForItPromise(duration({ seconds: 5 }));
        sounds.sfx.stop('timerFast');

        // Time is up, play SFX and disable matching.
        sounds.sfx.play('timerEnd');
        StateObserver.dispatch(setPlaying(false));

        // Wait until matching animation is finished
        await statePromise(({ match3 }) => !match3.matching);

        // Wait a bit more.
        await waitForItPromise(duration({ seconds: 1 }));

        // Show time up overlay.
        StateObserver.dispatch(setPuzzleReadyState('finish'));
        await waitForItPromise(duration({ seconds: 3 }));

        // Hide the overlay.
        StateObserver.dispatch(setPuzzleReadyState('hidden'));
        await waitForItPromise(animDuration);

        return false;
      },

      // Return to living room scene and claim reward
      async () => {
        sounds.music.play('bgm');

        const pet = getPet();
        const { appearance } = pet;
        await pet.moveTo({
          scene: 'livingRoom',
          location: 'c',
        });
        appearance.setAction(null);

        const { user } = StateObserver.getState();
        const { points, gems } = Match3.getters(user).getTotalRewards();
        const { coins } = Match3.ruleset.getRewardAmounts(points);
        await openPopupPromise('popupMinigameResults', {
          points,
          gems,
          coins,
        });
        await StateObserver.invoke.match3ConsumeTotalReward();

        analytics.pushEvent('MinigameFinish', {
          gameName: minigame,
          score: points,
          coinsEarned: coins,
          gemsEarned: gems,
        });

        return false;
      },

      // Show friend invite popup when the player closes the mini game result screen if:
      // The energy balance is zero
      // Friend invite popup has not shown because of the same reason in the same session
      async () => {
        const now = StateObserver.now();
        const { user, social } = StateObserver.getState();

        const hasNotEnergy = Inventory.getters(user).getEnergy(now) === 0;
        const hasNotSeenInvite = !social.hasSeenInvitePopup;
        const showInviteEnabled = false;

        const shouldShowInvitePopupOnRunOutOfEnergy =
          showInviteEnabled && hasNotEnergy && hasNotSeenInvite;

        if (shouldShowInvitePopupOnRunOutOfEnergy) {
          // Queue up invite sequence.
          void ActionSequence.start(
            socialSequences.invite(Feature.INVITE.INVITE_NO_ENERGY),
            'Invite',
          );

          // Do not show it again in same game session
          StateObserver.dispatch(socialActions.setHasSeenInvitePopup(true));
        }

        return false;
      },

      // Check if invite friends feature is unlocked.
      async () => {
        const { user } = StateObserver.getState();

        const { isTutorialStepComplete } = Tutorial.getters(user);

        // Tutorial already completed, bail.
        if (isTutorialStepComplete('friendReferral')) {
          return true;
        }

        if (!Social.getters(user).isFriendInviteAvailable()) return true;

        // Feature not unlocked, bail.
        const { minigamesToUnlockInvite: requiredMinigames } = Social.ruleset;
        if (Minigame.getters(user).getMinigameCount() < requiredMinigames) {
          return true;
        }

        // Queue up invite sequence.
        void ActionSequence.start(
          socialSequences.invite(Feature.INVITE.INVITE_UNLOCK),
          'Invite',
        );

        return false;
      },
    ],
    `StartMinigame:${minigame}`,
  );
}

async function loadMinigame(type: MinigameType) {
  switch (type) {
    case 'match3':
      await Promise.all([
        waitForAssets(match3Assets),
        StateObserver.invoke.match3ResetBoard(),
      ]);
      sounds.music.play('match3');
      StateObserver.dispatch(
        match3Actions.setBoardState(
          Match3.getters(StateObserver.getState().user).getBoardState(),
        ),
      );
      break;

    default:
      assertNever(type);
  }
}

export async function unlockMinigame() {
  // No need to bind if minigames are already available.
  const { user } = StateObserver.getState();
  const { areMinigamesAvailable } = Minigame.getters(user);

  if (user.minigame.minigameShown || !areMinigamesAvailable()) {
    return;
  }

  await StateObserver.invoke.setMinigameShown();
  const selectedMinigame = await openPopupPromise('popupSelectMinigame', {});

  // Start selected minigame.
  if (selectedMinigame) {
    await startMinigame(selectedMinigame);
  }
}
